import { useEffect, useState } from "react";
import type { useChat } from "../../controllers/useChat";
import { useAccount } from "../../controllers/useAccount";
import { useAuth } from "../../controllers/AuthProvider";
import { useUserDirectory } from "../../controllers/useUserDirectory";
import type { ChatChannel } from "../../models/project";

/** Anyone who can be added to a channel — a project or workspace member (only userId is needed). */
export type ChatMemberOption = { userId: string };

type Dir = ReturnType<typeof useUserDirectory>;

/**
 * A group shows its name; a DM shows the OTHER person. Every DM used to render the same "1:1 대화",
 * so a list of them was unusable and the name of the person you were talking to appeared nowhere —
 * against the rule that people are never shown as anything but their name.
 */
function channelLabel(c: ChatChannel, myUserId: string | null, dir: Dir): string {
  if (c.type === "GROUP") return c.name ?? "그룹 채널";
  const peer = (c.memberIds ?? []).find((id) => id !== myUserId);
  return peer ? dir.label(peer) : "1:1 대화";
}

// A chat surface (DM + group channels, "단톡"): channel list on the left, messages + composer on the
// right, start-DM / create-group, realtime updates via useChat's /ws/chat socket. People are shown by
// name/affiliation/email (resolved via useUserDirectory), never raw user ids.
export function ChatView({
  chat,
  members,
}: {
  chat: ReturnType<typeof useChat>;
  members: ChatMemberOption[];
}) {
  const { account } = useAccount();
  const { token } = useAuth();
  const dir = useUserDirectory(token);
  const [body, setBody] = useState("");
  const [starting, setStarting] = useState<"dm" | "group" | null>(null);

  useEffect(() => {
    dir.ensure([
      ...members.map((m) => m.userId),
      ...chat.messages.map((m) => m.senderId),
      // DM peers: a channel's participants may not be in `members` (a workspace DM with someone who
      // is not in the project roster this surface was given), and their name is the channel's label.
      ...chat.channels.flatMap((c) => c.memberIds ?? []),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, chat.messages, chat.channels]);

  async function submit() {
    if (!body.trim()) return;
    await chat.send(body);
    setBody("");
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>채팅 {chat.socketConnected ? <span className="badge ok">실시간 연결됨</span> : <span className="badge locked">오프라인</span>}</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => setStarting(starting === "dm" ? null : "dm")}>＋ DM 시작</button>
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => setStarting(starting === "group" ? null : "group")}>＋ 그룹 생성</button>
        </div>
      </div>

      {chat.error && <div className="note" style={{ background: "#fdeef0", marginBottom: 12 }}><b style={{ color: "var(--amber)" }}>오류:</b> {chat.error}</div>}

      {starting === "dm" && <StartDirect chat={chat} members={members} dir={dir} onDone={() => setStarting(null)} />}
      {starting === "group" && <CreateGroup chat={chat} members={members} dir={dir} onDone={() => setStarting(null)} />}

      <div className="chat-layout">
        <div className="chat-channels">
          {chat.channels
            .slice()
            // Newest conversation first. A channel nobody has posted in has no lastMessageAt at all,
            // so it sorts last — reading it off the object and calling localeCompare on the result
            // blanked the entire shell as soon as two such channels existed.
            .sort((a, b) => (b.lastMessageAt ?? "").localeCompare(a.lastMessageAt ?? ""))
            .map((c) => (
              <div
                key={c.id}
                className={`chat-channel-row ${chat.selectedChannelId === c.id ? "active" : ""} ${chat.unreadChannelIds.has(c.id) ? "unread" : ""}`}
                onClick={() => chat.selectChannel(c.id)}
              >
                {channelLabel(c, account?.id ?? null, dir)}
              </div>
            ))}
          {chat.channels.length === 0 && <div className="sdui-list-empty">채널이 없습니다.</div>}
        </div>

        <div className="chat-main">
          {!chat.selectedChannelId ? (
            <div className="center">채널을 선택하세요</div>
          ) : (
            <>
              <div className="chat-messages">
                {chat.messages.map((m) => (
                  <div key={m.id} className={`chat-bubble ${m.senderId === account?.id ? "mine" : ""}`}>
                    <div className="mono" style={{ fontSize: 11, opacity: 0.6 }}>{m.senderId === account?.id ? "나" : dir.label(m.senderId)} · {new Date(m.createdAt).toLocaleTimeString()}</div>
                    {m.body}
                  </div>
                ))}
                {chat.messages.length === 0 && <div className="sdui-list-empty">메시지가 없습니다.</div>}
              </div>
              <div className="chat-composer">
                <input
                  className="sdui-input"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") void submit(); }}
                  placeholder="메시지 입력"
                />
                <button className="btn primary" style={{ width: "auto" }} disabled={!body.trim()} onClick={submit}>전송</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StartDirect({
  chat, members, dir, onDone,
}: { chat: ReturnType<typeof useChat>; members: ChatMemberOption[]; dir: Dir; onDone: () => void }) {
  const [userId, setUserId] = useState("");
  return (
    <div className="info-card" style={{ marginBottom: 12 }}>
      <h4>DM 시작</h4>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <label className="sdui-field" style={{ flex: 1 }}>
          <span className="sdui-label">상대방</span>
          <select className="sdui-input" value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">선택</option>
            {members.map((m) => <option key={m.userId} value={m.userId}>{dir.detail(m.userId)}</option>)}
          </select>
        </label>
        <button className="btn primary" style={{ width: "auto" }} disabled={!userId} onClick={async () => { await chat.openDirect(userId); onDone(); }}>시작</button>
      </div>
    </div>
  );
}

function CreateGroup({
  chat, members, dir, onDone,
}: { chat: ReturnType<typeof useChat>; members: ChatMemberOption[]; dir: Dir; onDone: () => void }) {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  function toggle(userId: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  }

  return (
    <div className="info-card" style={{ marginBottom: 12 }}>
      <h4>그룹 채널 생성</h4>
      <label className="sdui-field">
        <span className="sdui-label">이름</span>
        <input className="sdui-input" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "8px 0" }}>
        {members.map((m) => (
          <button
            key={m.userId}
            className="tag"
            style={{ border: selected.has(m.userId) ? "1px solid var(--blue)" : "none", color: selected.has(m.userId) ? "var(--blue)" : undefined }}
            onClick={() => toggle(m.userId)}
          >
            {dir.label(m.userId)}
          </button>
        ))}
      </div>
      <button
        className="btn primary" style={{ width: "auto" }}
        disabled={!name.trim() || selected.size === 0}
        onClick={async () => { await chat.createGroup({ name, memberIds: [...selected] }); onDone(); }}
      >생성</button>
    </div>
  );
}
